import { useGetCommissionsQuery, useUpdateCommissionMutation } from "api/TenantSlug/Commission/hooks.ts";
import { tableAtom } from "components/UI/Table/atoms/tableAtom.ts";
import { FILTER_TYPES } from "components/UI/Table/constants/filter.ts";
import { TableColumnType } from "components/UI/Table/types/table.ts";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell.tsx";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell.tsx";
import { ComposedValue } from "components/UI/Table/UI/InteractiveCell/components/WithPayoutFormula.tsx";
import { InteractiveCell } from "components/UI/Table/UI/InteractiveCell/InteractiveCell.ts";
import { PayoutCell } from "components/UI/Table/UI/PayoutCell/PayoutCell.tsx";
import { StatusCell } from "components/UI/Table/UI/StatusCell/StatusCell.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions.ts";
import { useUser } from "hooks/useUser.ts";
import { COMMISSIONS_PRIORITY_SORT } from "localConstants.ts";
import React, { FC, useMemo } from "react";
import { useRecoilValue } from "recoil";
import { CommissionType, PermissionEnum } from "types/enums.ts";
import { DefaultStatus, TableId } from "types/models.ts";
import { updateChildCommission, updateParentCommission } from "utils/commission/updateCommission";

import { SimpleCommissionsTable } from "./SimpleCommissionsTable.tsx";
import { userGroup } from "../CommissionGroupTable/temp.ts";

import CommissionData = App.Data.CommissionData;
import CascadeData = App.Data.CascadeData;

interface Props {
    offer?: App.Data.OfferData;
    cascade?: CascadeData;
    commissionGroupId?: string | null;
    editPermission?: PermissionEnum;
    showProvisionGroups?: boolean;
    showOnlyEnabledCommissions?: boolean;
    allowAddCommission?: boolean;
    expandButtonRef?: React.RefObject<() => void>;
    scrollY?: number | string;
    /** Always show the read-only display amount (PayoutCell), even if the viewer has editPermission.
     *  For preview/detail views where the editable formula input shouldn't be shown regardless of role. */
    readOnlyPayout?: boolean;
}

export const SimpleCommissionsTableContainer: FC<Props> = ({
    offer,
    cascade,
    commissionGroupId,
    editPermission,
    showOnlyEnabledCommissions,
    allowAddCommission,
    expandButtonRef,
    scrollY,
    readOnlyPayout,
}) => {
    const { t } = usePaldockTranslation("tables.commissions-table");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tC } = usePaldockTranslation("common");
    const permissionsUtils = usePermission();
    const user = useUser();
    const { filter, page } = useRecoilValue(tableAtom<CommissionData>()(TableId.CommissionsTable));
    const refetchFromChildRef = React.useRef<() => void>();

    const fetchCommissionListQuery = useGetCommissionsQuery("commissions", {
        page: { number: page?.number, size: page?.size },
        filter: {
            filter: {
                ...(offer
                    ? {
                          "offer.id": offer.id,
                          ...((user.isAffiliate() || showOnlyEnabledCommissions) && { is_enabled: true }),
                      }
                    : {}),
                ...(cascade ? { "offer.id": cascade.offer_id, cascade_id: cascade.id } : {}),
                ...(filter?.filter ?? {}),
                commission_type:
                    commissionGroupId || user.isAffiliate()
                        ? CommissionType.User
                        : cascade
                          ? undefined
                          : CommissionType.Tenant,
                ...(commissionGroupId && {
                    commission_group_id: commissionGroupId === userGroup.id ? "" : commissionGroupId,
                }),
            },
            sort: filter?.sort ?? COMMISSIONS_PRIORITY_SORT,
            include:
                "offer,user,cascade,parent,parent.user,offer.country,children,children.commissionGroup,commissionGroup",
        },
    });

    const { mutateAsync: updateCommission } = useUpdateCommissionMutation();

    const updateChild = (value: ComposedValue, record: CommissionData, childId: string) => {
        return updateChildCommission(value, record, childId, updateCommission, {
            onSuccess: () => {
                fetchCommissionListQuery.refetch();
                refetchFromChildRef.current?.();
            },
            successMessage: t("messages.update_success"),
            errorMessage: t("messages.update_error"),
            notFoundMessage: t("messages.commission_not_found"),
        });
    };

    const updateParent = (value: ComposedValue, record: CommissionData) => {
        return updateParentCommission(value, record, updateCommission, {
            onSuccess: () => {
                fetchCommissionListQuery.refetch();
                refetchFromChildRef.current?.();
            },
            successMessage: t("messages.update_success"),
            errorMessage: t("messages.update_error"),
        });
    };

    const firstCommission = fetchCommissionListQuery.data?.commissions[0];

    const columns = useMemo(() => {
        const transactionColumn: TableColumnType<CommissionData> = {
            title: <ColumnHeaderTitle columnTitle={tG("transaction")} />,
            dataIndex: "autocreate_conversion_type",
            key: "autocreate_conversion_type",
            sorter: false,
            render: (_, data) => {
                let conversionType;
                if (!data.autocreate_conversion_type || data.autocreate_conversion_type == "") {
                    conversionType = "sale";
                } else {
                    conversionType = data.autocreate_conversion_type;
                }
                return (
                    <>
                        {conversionType} {data.is_autoapprove && user.isAdmin() ? " ⚡" : ""}
                    </>
                ); // todo not light for affil
            },
            ellipsis: true,
            width: 120,
            filterable: true,
            filterType: FILTER_TYPES.string,
        };

        const payoutFormulaColumn: TableColumnType<CommissionData> = {
            title: !(editPermission && permissionsUtils.hasPermission(editPermission))
                ? t("columns.commission")
                : t("columns.workspace"),
            dataIndex: "payout_formula",
            key: "payout_formula",
            sorter: false,
            render: (formula: string, record: CommissionData) =>
                formula ? (
                    readOnlyPayout || !(editPermission && permissionsUtils.hasPermission(editPermission)) ? (
                        <PayoutCell commission={record} />
                    ) : (
                        <InteractiveCell.PayoutFormula
                            type={"input"}
                            defaultValue={{
                                payoutFormula: formula,
                                formulaType: record.formula_type as "formula" | "percent" | "fix",
                                isEnabled: record.is_enabled,
                            }}
                            onSave={(value) => updateParent(value, record)}
                            currencyId={record.offer?.currency_id}
                            isEnabled={record.is_enabled}
                        />
                    )
                ) : (
                    <EmptyCell />
                ),
            width: 130,
            ellipsis: true,
        };

        const restColumns: TableColumnType<CommissionData>[] = [
            {
                title: tG("source"),
                dataIndex: "conversion_source",
                key: "conversion_source",
                sorter: false,
                render: (source: string) => <EmptyCell>{source}</EmptyCell>,
                width: 120,
                ellipsis: true,
            },
            ...(user.isAdmin() && !cascade
                ? [
                      {
                          title: tG("channel"),
                          dataIndex: "cascade.title",
                          key: "cascade.title",
                          sorter: false,
                          render: (_: string, data: CommissionData) => <EntityLinkCell.Cascade entity={data.cascade} />,
                          ellipsis: true,
                          width: 150,
                          filterable: true,
                          filterType: FILTER_TYPES.string,
                      },
                  ]
                : []),
            {
                title: tG("description"),
                dataIndex: "description",
                key: "description",
                sorter: false,
                render: (description: string) => <EmptyCell>{description}</EmptyCell>,
                width: 200,
                ellipsis: true,
            },
        ];

        if (user.isAdmin() && !showOnlyEnabledCommissions) {
            restColumns.push({
                title: tG("status"),
                dataIndex: "is_enabled",
                key: "is_enabled",
                inputType: "text",
                render: (status: boolean) => (
                    <StatusCell
                        status={status ? DefaultStatus.Success : DefaultStatus.Error}
                        hideLabel={true}
                        customLabels={{
                            [DefaultStatus.Success]: tC("active"),
                            [DefaultStatus.Error]: tC("paused"),
                        }}
                    />
                ),
                filterable: true,
                filterType: FILTER_TYPES.select,
                filterOptions: [
                    { label: tC("active"), value: true },
                    { label: tC("paused"), value: false },
                ],
                sorter: false,
                width: 50,
                ellipsis: true,
            });
        }

        if (!firstCommission || (editPermission && !permissionsUtils.hasPermission(editPermission))) {
            return [transactionColumn, payoutFormulaColumn, ...restColumns];
        }

        const transformedCommissions = firstCommission.children?.reduce(
            (acc, child) => {
                if (child.commission_group_id) {
                    acc[child.commission_group_id] = child;
                } else {
                    acc["default"] = child;
                }

                return acc;
            },
            {} as Record<string, App.Data.CommissionData>
        );

        const dynamicColumns = Object.entries(transformedCommissions || {})
            ?.sort(([groupIdA, childA], [groupIdB, childB]) => {
                if (groupIdA === "default") return -1;
                if (groupIdB === "default") return 1;

                const titleA = childA.commissionGroup?.title ?? "";
                const titleB = childB.commissionGroup?.title ?? "";

                return titleA.localeCompare(titleB);
            })
            .map(([groupId, child]) => ({
                title: child.commissionGroup?.title ?? t("columns.default_group"),
                dataIndex: [`children`],
                key: child.commissionGroup?.title ?? t("columns.default_group"),
                render: (children: CommissionData[], record: CommissionData) => {
                    // Find child by commission_group_id, not by index
                    const matchingChild = children?.find((c) =>
                        groupId === "default" ? !c.commission_group_id : c.commission_group_id === groupId
                    );

                    if (!matchingChild) {
                        return <EmptyCell />;
                    }

                    // WithPayoutFormula doesn't honor a `readOnly` prop (it's spread onto the
                    // underlying <Input> as a DOM attribute, which doesn't stop the type Select or
                    // TagTextInput from being interactive) — swap to the plain PayoutCell instead.
                    if (readOnlyPayout) {
                        return <PayoutCell commission={matchingChild} />;
                    }

                    return (
                        <InteractiveCell.PayoutFormula
                            defaultValue={{
                                payoutFormula: matchingChild.payout_formula || "",
                                formulaType: matchingChild.formula_type as "formula" | "percent" | "fix",
                                isEnabled: matchingChild.is_enabled,
                            }}
                            onSave={(value) => updateChild(value, record, matchingChild.id)}
                            currencyId={record.offer?.currency_id}
                            isEnabled={matchingChild.is_enabled}
                        />
                    );
                },
                width: 130,
                ellipsis: true,
            }));

        if (dynamicColumns?.length === 0) {
            return [transactionColumn, payoutFormulaColumn, ...restColumns];
        }

        return [transactionColumn, payoutFormulaColumn, ...dynamicColumns, ...restColumns];
    }, [
        fetchCommissionListQuery.data?.commissions,
        editPermission,
        permissionsUtils,
        user,
        cascade,
        t,
        tG,
        tC,
        showOnlyEnabledCommissions,
        readOnlyPayout,
    ]);

    if (!columns) {
        return <></>;
    }

    return (
        <SimpleCommissionsTable
            editPermission={editPermission}
            columns={columns}
            expandedColumns={columns}
            commissionGroupId={commissionGroupId}
            offer={offer}
            cascade={cascade}
            showOnlyEnabledCommissions={showOnlyEnabledCommissions}
            allowAddCommission={allowAddCommission}
            onRefetchTable={(fn) => {
                refetchFromChildRef.current = fn;
            }}
            isLoading={fetchCommissionListQuery.isLoading || fetchCommissionListQuery.isFetching}
            expandButtonRef={expandButtonRef}
            scrollY={scrollY}
        />
    );
};
